import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AsyncBoundary } from '@kollez/design-system';
import { apiGet } from '@/lib/api';
import { formatIndianDate } from '@kollez/shared';

interface AttemptRow {
  _id: string;
  assessmentId?: { _id: string; title: string; category?: string; difficulty?: string } | string;
  startedAt?: string;
  submittedAt?: string;
  status?: string;
  result?: { percentScore?: number; pass?: boolean };
  integrityScore?: number;
}

function examTitle(a: AttemptRow): string {
  const e = a.assessmentId;
  if (!e) return '—';
  return typeof e === 'string' ? e : e.title;
}

/**
 * /tests — student's exam attempts (AssessmentAttempt list).
 *
 * Deep-audit fixes (Page 14/N):
 *   - No pass-rate rollup — student had to eyeball their history to know
 *     how they were doing. Added a tiny KPI header showing total /
 *     passed / in-progress with counts.
 *   - Integrity score '85/100' appeared without any explanation of what
 *     it measures. Title tooltip on the column header now describes it
 *     (higher is cleaner; below 60 flags for reviewer attention).
 */
export function StudentTestsPage() {
  const query = useQuery({
    queryKey: ['student', 'me', 'tests'],
    queryFn: () => apiGet<{ rows: AttemptRow[] }>('/students/me/tests'),
  });
  const rows = query.data?.rows ?? [];

  const submitted = rows.filter((r) => r.submittedAt);
  const inProgress = rows.filter((r) => !r.submittedAt);
  const passed = submitted.filter((r) => r.result?.pass === true).length;
  const failed = submitted.filter((r) => r.result?.pass === false).length;

  return (
    <div className="stack">
      <div className="page-header">
        <div className="ph-text">
          <p className="eyebrow">Get ready</p>
          <h1 className="h-page">Tests</h1>
          <p className="subhead">
            Every test you have started or finished. Retakes update the same row.
            {rows.length > 0 && (
              <> {' · '}<b>{submitted.length}</b> submitted · <b>{passed}</b> passed · <b>{failed}</b> below cut-off · <b>{inProgress.length}</b> in progress</>
            )}
          </p>
        </div>
        <div className="ph-actions">
          <Link className="btn btn-outline" to="/mock">Take a practice mock</Link>
        </div>
      </div>

      <AsyncBoundary
        kind="table"
        loading={query.isLoading}
        error={query.error}
        empty={rows.length === 0}
        emptyTitle="No test attempts yet"
        emptyHint="Assessments assigned by your placement office or company screen tests will show up here."
      >
        {inProgress.length > 0 && (
          <section className="card">
            <div className="card-head"><h2 className="h-section">In progress</h2></div>
            <div className="tbl-wrap">
              <table className="tbl">
                <thead><tr><th>Exam</th><th className="hide-mobile">Started</th><th className="r">Resume</th></tr></thead>
                <tbody>
                  {inProgress.map((a) => (
                    <tr key={a._id}>
                      <td className="cell-title">{examTitle(a)}</td>
                      <td className="hide-mobile cell-sub">{a.startedAt ? formatIndianDate(new Date(a.startedAt)) : '—'}</td>
                      <td className="r"><Link className="btn btn-primary btn-sm" to={`/assess/take/${typeof a.assessmentId === 'object' ? a.assessmentId._id : a.assessmentId}`}>Resume</Link></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {submitted.length > 0 && (
          <section className="card">
            <div className="card-head"><h2 className="h-section">Past attempts</h2></div>
            <div className="tbl-wrap">
              <table className="tbl">
                <thead><tr>
                  <th>Exam</th>
                  <th className="r">Score</th>
                  <th
                    className="hide-mobile"
                    title="Integrity score summarizes proctoring signals during the attempt (face-detect, tab-switches, paste attempts). Higher is cleaner; below 60 is flagged for reviewer attention."
                  >
                    Integrity
                  </th>
                  <th className="hide-mobile">Submitted</th>
                  <th></th>
                </tr></thead>
                <tbody>
                  {submitted.map((a) => (
                    <tr key={a._id}>
                      <td className="cell-title">{examTitle(a)}</td>
                      <td className="r">
                        {a.result?.percentScore != null ? `${Math.round(a.result.percentScore)}%` : '—'}
                        {a.result?.pass != null && <span className={`badge ml-2 ${a.result.pass ? 'badge-success' : 'badge-warning'}`}>{a.result.pass ? 'Pass' : 'Below cut-off'}</span>}
                      </td>
                      <td className="hide-mobile cell-sub">{a.integrityScore != null ? `${a.integrityScore}/100` : '—'}</td>
                      <td className="hide-mobile cell-sub">{a.submittedAt ? formatIndianDate(new Date(a.submittedAt)) : '—'}</td>
                      <td className="r"><Link className="btn btn-ghost btn-sm" to={`/assess/attempts/${a._id}`}>Review</Link></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}
      </AsyncBoundary>
    </div>
  );
}
